import { useDisclosure } from "@mantine/hooks";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { logger } from "#app/monitoring/Logger";
import { colors } from "#app/theme/v1/colors";
import { useCompany } from "#modules/company/view/useCompany";
import { useCompanyMachines } from "#modules/machines/view/useCompanyMachines";
import { useOptimizedMachineTimes } from "#modules/optimizations/view/useOptimizedMachineTimes";
import { UpdateProcessStepResponseDto } from "#modules/processSteps/infra/updateProcessStep.api";
import { useDeleteProcessStep } from "#modules/processSteps/view/useDeleteProcessStep";
import { useUpdateProcessStep } from "#modules/processSteps/view/useUpdateProcessStep";
import { Alert } from "#modules/quoteRequestLines/domain/alert";
import { QuoteRequestLineFormData } from "#modules/quoteRequestLines/types/QuoteRequestLineFormData";
import { useQuoteRequestLine } from "#modules/quoteRequestLines/view/useQuoteRequestLine";
import { ProcessStepForTable } from "#modules/quoteRequestLines/views/DetailsPanel/ProcessSteps/ProcessStepsTable";
import { getIndustrialProcessWording } from "#shared/helpers/getIndustrialProcessWording";
import { Icon } from "#shared/icons/Icon";
import { useAnalytics, AnalyticsEvent } from "#shared/useAnalytics";
import { ActionIcon } from "#shared/view/components/ActionIcon";
import { getProcessStepTitle } from "#shared/view/components/Form/getProcessStepTitle";
import { Group } from "#shared/view/components/Group";
import { Popover } from "#shared/view/components/Popover";
import { Text } from "#shared/view/components/Text";
import { Table } from "#shared/view/designSystem/Table";
import { Tooltip } from "#shared/view/designSystem/Tooltip";
import {
  computeProcessPrice,
  formatPrice,
  formatTimeString,
  IndustrialProcessSchema,
  IndustrialProcessType,
  ProcessStepModeSchema,
  ProcessStep as ProcessStepType,
  TimeDisplayFormatSchema,
  withOptimizedMachineTimes,
} from "shared";

import { CompanyMachineInput } from "./CompanyMachineInput";
import { EditableProcessStepCell } from "./EditableProcessStepCell";
import { TimeProcessStepForm } from "./TimeProcessStepForm";

export type PartialProcessStep = Omit<Partial<ProcessStepType>, "industrialProcess" | "index"> & {
  industrialProcess: IndustrialProcessType;
  index: number;
};

export const ProcessStep = ({
  showCompanyMachines,
  processStep: processStepWithoutOptimizedMachineTimes,
  index,
  onProcessStepUpdated,
  onProcessStepDeleted,
  readonly = false,
  renderAsFragment = false,
  alerts = [],
}: {
  showCompanyMachines: boolean;
  readonly?: boolean;
  processStep: ProcessStepForTable;
  index: number;
  onProcessStepUpdated?: (updatedProcessStep: UpdateProcessStepResponseDto) => void;
  onProcessStepDeleted?: () => void;
  renderAsFragment?: boolean;
  alerts?: Alert[];
}) => {
  const [
    isOpen,
    { open: openProcessStepModal, close: closeProcessStepModal, toggle: toggleProcessStepModal },
  ] = useDisclosure(false);
  const { t } = useTranslation();
  const analytics = useAnalytics();
  const { control, setValue } = useFormContext<QuoteRequestLineFormData>();
  const { remove } = useFieldArray({
    control,
    name: "processSteps",
  });

  const { deleteProcessStep } = useDeleteProcessStep({
    onSuccess: onProcessStepDeleted,
  });

  const { updateProcessStep } = useUpdateProcessStep({
    onSuccess: onProcessStepUpdated,
  });

  const { companyMachines } = useCompanyMachines();

  const { company } = useCompany();

  const part = useWatch({ control, name: "part" });
  const materialGrade = useWatch({ control, name: "materialGrade" });
  const rawQuantity = useWatch({ control, name: "quantity" });
  const reworkRatio = useWatch({ control, name: "reworkRatio" });
  const quoteRequestLineId = useWatch({ control, name: "quoteRequestLineId" });
  const quantity = rawQuantity ?? 1;
  const { quoteRequestLine } = useQuoteRequestLine({ id: quoteRequestLineId });
  const optimizedMachineTimes = useOptimizedMachineTimes(quoteRequestLine?.quoteRequestId);
  const currentCompanyMachine = quoteRequestLine?.processSteps.find(
    ({ id }) => id === processStepWithoutOptimizedMachineTimes.id,
  )?.companyMachine;

  const processStepPrice = computeProcessPrice({
    quantity: quantity,
    quantityInAssembly: part?.quantityInAssembly || 1,
    reworkRatio,
    processStep: processStepWithoutOptimizedMachineTimes,
    optimizedMachineTimes,
  }).partProcessCost;

  const processStep = withOptimizedMachineTimes(
    processStepWithoutOptimizedMachineTimes,
    optimizedMachineTimes,
  );

  const removeProcessStep = () => {
    analytics.capture(AnalyticsEvent.DetailsPanelProcessStepRemoved, {
      quote_request_line_id: quoteRequestLineId,
      process_step_id: processStep.id,
      industrial_process: processStep.industrialProcess,
    });

    if (processStep.id) {
      deleteProcessStep({ id: processStep.id });
    }
    remove(index);
  };

  const processStepAlert = alerts.find((alert) => alert.processStepId === processStep.id);

  const title = getProcessStepTitle({ processStep, companyMachine: undefined, materialGrade });

  const shouldShowProcessStepModal =
    processStep.mode === ProcessStepModeSchema.enum.TIME ||
    processStep.industrialProcess === IndustrialProcessSchema.enum.ASSEMBLING ||
    processStep.industrialProcess === IndustrialProcessSchema.enum.SURFACE_TREATMENT ||
    processStep.industrialProcess === IndustrialProcessSchema.enum.CONTROL ||
    processStep.industrialProcess === IndustrialProcessSchema.enum.PACKING;

  const showTimeValues =
    !processStep.fixedCost &&
    !processStep.fixedCostPerSeries &&
    processStep.industrialProcess !== IndustrialProcessSchema.enum.OUTSOURCING;

  const canEditTimeValues =
    showTimeValues &&
    processStep.mode === ProcessStepModeSchema.enum.TIME &&
    (processStep.fixedCostPerSeries || 0) <= 0;

  const handleUpdateField = (field: keyof PartialProcessStep, value: number | string | null) => {
    setValue(`processSteps.${index}.${field}`, value);

    analytics.capture(AnalyticsEvent.DetailsPanelProcessStepEdited, {
      quote_request_line_id: quoteRequestLineId,
      industrial_process: processStep.industrialProcess,
      field,
    });

    if (processStep.id) {
      updateProcessStep({
        id: processStep.id,
        payload: { [field]: value },
      });
    }
  };

  const getDescriptionDisplayValue = () => {
    return processStep.description || "";
  };

  const getDescriptionPlaceholder = () => {
    return getIndustrialProcessWording(processStep.industrialProcess) || title;
  };

  const processStepName = processStep.description || getDescriptionPlaceholder();

  const timeDisplayFormat =
    company?.companySettings.timeDisplayFormat ?? TimeDisplayFormatSchema.enum.HH_MM_SS;

  const cells = (
    <>
      {!readonly && (
        <Table.Td w="5%">
          <ActionIcon size="xs" c={"dimmed"} variant="subtle" onClick={() => removeProcessStep()}>
            <Icon name="trash" size={14} />
          </ActionIcon>
        </Table.Td>
      )}
      <Table.Td w={showCompanyMachines ? "20%" : "30%"} style={{ overflow: "hidden" }}>
        {!readonly ? (
          <EditableProcessStepCell
            value={getDescriptionDisplayValue()}
            formatDisplayValue={() => processStepName}
            onCommit={(value) => handleUpdateField("description", value as string | null)}
            type="text"
            dataTestId={`description-${index}`}
          />
        ) : (
          title
        )}
      </Table.Td>
      {showCompanyMachines && (
        <Table.Td w="15%" style={{ overflow: "hidden" }}>
          <CompanyMachineInput
            industrialProcess={processStep.industrialProcess}
            processStepId={processStep.id}
            companyMachines={companyMachines}
            companyMachineId={processStep.companyMachineId}
            currentCompanyMachine={currentCompanyMachine}
            quoteRequestLineId={quoteRequestLineId}
          />
        </Table.Td>
      )}
      <Table.Td w="15%" style={{ overflow: "hidden" }}>
        {showTimeValues ? (
          <EditableProcessStepCell
            value={processStep.machinePreparationTime}
            formatDisplayValue={(v) => {
              if (typeof v === "string") {
                logger.error(`Error in formatDisplayValue: v is a string : ${v}`);
                return "-";
              }
              return formatTimeString(v, timeDisplayFormat);
            }}
            onCommit={(value) =>
              handleUpdateField("machinePreparationTime", value as number | null)
            }
            type="time"
            disabled={readonly || !canEditTimeValues}
            min={0}
            dataTestId={`machinePreparationTime-${index}`}
            timeDisplayFormat={timeDisplayFormat}
          />
        ) : (
          "-"
        )}
      </Table.Td>
      <Table.Td w="15%" style={{ overflow: "hidden" }}>
        {showTimeValues ? (
          <EditableProcessStepCell
            value={processStep.otherMachineTime}
            formatDisplayValue={(v) => {
              if (typeof v === "string") {
                logger.error(`Error in formatDisplayValue: v is a string : ${v}`);
                return "-";
              }
              return formatTimeString(v, timeDisplayFormat);
            }}
            onCommit={(value) => handleUpdateField("otherMachineTime", value as number | null)}
            type="time"
            disabled={readonly || !canEditTimeValues}
            min={0}
            dataTestId={`otherMachineTime-${index}`}
            timeDisplayFormat={timeDisplayFormat}
          />
        ) : (
          "-"
        )}
      </Table.Td>
      <Table.Td w="10%">
        {showTimeValues ? (
          <EditableProcessStepCell
            value={processStep.machineHourlyPrice}
            formatDisplayValue={(v) => formatPrice(v as number | null | undefined)}
            onCommit={(value) => handleUpdateField("machineHourlyPrice", value as number | null)}
            type="price"
            disabled={readonly || !canEditTimeValues}
            suffix=" €"
            dataTestId={`machineHourlyPrice-${index}`}
          />
        ) : (
          "-"
        )}
      </Table.Td>
      <Table.Td w="10%">
        <Text fw={700} c={colors.violet[5]}>
          {formatPrice(processStepPrice)}
        </Text>
      </Table.Td>
      {!readonly && (
        <Table.Td w="5%">
          <Group gap={"xxs"} justify="end" wrap="nowrap">
            {processStepAlert && (
              <Tooltip
                multiline
                withArrow
                label={
                  <Text>
                    {t(
                      `alert.${processStepAlert.message}`,
                      processStepAlert.messageParameters || undefined,
                    )}
                  </Text>
                }
              >
                <Icon name="alertTriangle" size={14} color={colors.orange[7]} />
              </Tooltip>
            )}
            {shouldShowProcessStepModal && (
              <Popover
                width={300}
                position="bottom-end"
                withArrow
                arrowPosition="side"
                shadow="lg"
                disabled={!shouldShowProcessStepModal}
                opened={isOpen}
                onChange={toggleProcessStepModal}
                closeOnClickOutside={false}
              >
                <Popover.Target>
                  <Group gap={"xxs"} onClick={openProcessStepModal} style={{ cursor: "pointer" }}>
                    <Icon name="edit" size={14} color={colors.gray[5]} />
                  </Group>
                </Popover.Target>
                <Popover.Dropdown>
                  <TimeProcessStepForm
                    processStep={processStep}
                    materialGrade={materialGrade}
                    onClose={closeProcessStepModal}
                    onProcessStepUpdated={onProcessStepUpdated}
                    quoteRequestLineId={quoteRequestLineId}
                  />
                </Popover.Dropdown>
              </Popover>
            )}
          </Group>
        </Table.Td>
      )}
    </>
  );

  if (renderAsFragment) {
    return cells;
  }

  return <Table.Tr>{cells}</Table.Tr>;
};
